import { useQuery } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { Message } from "@/components/Playground/types";
import { useAuth } from "@/context/auth-context";
import { Content, PromptBlueprint } from "@/types";
import { RunStatus } from "@/types/metadata";
import { authHeader } from "@/utils/headers";

export interface RunGroupIndividualRunRequest {
  id: number;
  user_id: number;
  input_variables: Record<string, unknown>;
  is_chat: boolean;
  // Backend writes either a single Message dict (no-tools or single-message
  // run) or a list of Messages (auto-execution loop produced multiple).
  message: Message | Message[] | null;
  content: Content[] | null;
  request_id: number | null;
  status: RunStatus;
  status_message: string | null;
  created_at: string;
  finished_at: string | null;
  workspace_id: number;
  run_group_id: string;
  run_group_position: number;
  prompt_blueprint: PromptBlueprint | null;
  price: number | null;
}

export interface RunGroup {
  id: string;
  created_at: string;
  updated_at: string;
  playground_session_id: string;
  shared_prompt_blueprint: PromptBlueprint;
  status: RunStatus;
  is_in_progress: boolean;
  individual_run_requests: RunGroupIndividualRunRequest[];
}

export interface RunGroupsCursor {
  created_at: string;
  id: string;
}

interface GetRunGroupsResponse {
  success?: boolean;
  run_groups?: RunGroup[];
  has_next?: boolean;
  has_prev?: boolean;
  next_cursor?: RunGroupsCursor | null;
  total?: number;
}

export const useGetRunGroups = (
  workspaceId: number,
  sessionId: string,
  options?: { beforeCursor?: RunGroupsCursor | null; perPage?: number },
  forceRefresh?: unknown,
) => {
  const queryKey = [
    "runGroups",
    workspaceId,
    sessionId,
    options?.beforeCursor?.created_at,
    options?.beforeCursor?.id,
    options?.perPage,
    forceRefresh,
  ];

  const userToken = useAuth()?.userToken ?? "";

  return useQuery<GetRunGroupsResponse>({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({
        per_page: (options?.perPage || 10).toString(),
        playground_session_id: sessionId,
      });

      if (options?.beforeCursor) {
        params.set("before_created_at", options.beforeCursor.created_at);
        params.set("before_id", options.beforeCursor.id);
      }

      const response = await fetch(`${ENDPOINTS.run_groups(workspaceId)}?${params.toString()}`, {
        method: "GET",
        headers: authHeader(userToken),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch run groups");
      }

      return response.json();
    },
    keepPreviousData: true,
    refetchInterval: data => (data?.run_groups?.some(group => group.is_in_progress) ? 2000 : false),
  });
};
